/* @jsx h */
import { computed, defineComponent, h, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import type { CatalogDashboard } from "@app/api/trpc/routers/catalog";
import { AttentionItems } from "@app/vue/components/Dashboard/AttentionItems";
import { KpiTile } from "@app/vue/components/Dashboard/KpiTile";
import { WeeklyBars } from "@app/vue/components/Dashboard/WeeklyBars";
import { BestSellers } from "./BestSellers";
import { CatalogGlance } from "./CatalogGlance";
import { CategorySales } from "./CategorySales";
import { MenuCompleteness } from "./MenuCompleteness";
import { MenuMatrix } from "./MenuMatrix";
import { SlowMovers } from "./SlowMovers";
import { money } from "./format";
import {
  catalogAttention,
  engineerMenu,
  healthScore,
  menuMargin,
} from "./insights";
import { SubpageHeader } from "@app/vue/components/SubpageHeader";

const PERIODS = [7, 30, 90];

const changePct = (current: number, previous: number) =>
  previous > 0 ? ((current - previous) / previous) * 100 : null;

// The first page of the Catalog: what sells, what earns, and what in the
// catalog needs fixing. See docs/plans/catalog_editor/overview.md.
export const CatalogOverviewPage = defineComponent({
  name: "CatalogOverviewPage",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();
    const router = useRouter();

    const days = ref(30);
    const data = ref<CatalogDashboard | null>(null);
    const loading = ref(true);

    const load = async () => {
      loading.value = true;
      try {
        data.value = await trpc.catalog.dashboard.query({ days: days.value });
      } catch {
        toast.showToast(t("catalog.overview.load_failed"), { type: "error" });
      } finally {
        loading.value = false;
      }
    };
    onMounted(load);
    watch(days, load);

    const engineering = computed(() =>
      data.value ? engineerMenu(data.value.products) : null,
    );

    const card = (title: string, body: unknown, hint?: string) => (
      <section class="rounded-xl border border-base-300 bg-base-100 p-4">
        <div class="mb-3">
          <h3 class="text-sm font-semibold text-accent">{title}</h3>
          {hint && <p class="mt-0.5 text-xs text-base-content/50">{hint}</p>}
        </div>
        {body}
      </section>
    );

    const quick = (icon: string, label: string, to: string) => (
      <button
        class="btn btn-ghost btn-sm border-base-300"
        onClick={() => router.push(to)}
      >
        <span class="material-symbols-outlined text-base">{icon}</span>
        {label}
      </button>
    );

    const renderKpis = (d: CatalogDashboard) => {
      const margin = menuMargin(d.products);
      const health = healthScore(d.setup);
      const hidden = d.counts.out_of_stock + d.counts.unavailable;
      return (
        <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <KpiTile
            icon="payments"
            label={t("catalog.overview.sales")}
            value={money(d.sales.revenue)}
            change={changePct(d.sales.revenue, d.sales.previous_revenue)}
            hint={t("catalog.overview.items_sold").replace(
              "{count}",
              String(d.sales.items_sold),
            )}
          />
          <KpiTile
            icon="restaurant_menu"
            label={t("catalog.overview.on_menu")}
            value={String(d.counts.available)}
            hint={
              hidden > 0
                ? t("catalog.overview.hidden_hint")
                    .replace("{out}", String(d.counts.out_of_stock))
                    .replace("{off}", String(d.counts.unavailable))
                : t("catalog.overview.new_hint").replace(
                    "{count}",
                    String(d.counts.new_products),
                  )
            }
            to="/app/catalog_editor/products"
          />
          <KpiTile
            icon="percent"
            label={t("catalog.overview.margin")}
            value={
              margin.marginPct != null ? `${margin.marginPct.toFixed(1)}%` : "—"
            }
            hint={
              margin.costedShare != null && margin.marginPct != null
                ? t("catalog.overview.costed_share").replace(
                    "{pct}",
                    margin.costedShare.toFixed(0),
                  )
                : t("catalog.overview.add_recipes_hint")
            }
            to={
              d.setup.tracks_stock
                ? "/app/inventory/margins"
                : "/app/catalog_editor/products"
            }
          />
          <KpiTile
            icon="health_and_safety"
            label={t("catalog.overview.health")}
            value={health != null ? `${health.toFixed(0)}%` : "—"}
            hint={t("catalog.overview.health_hint")}
          />
        </div>
      );
    };

    return () => (
      <div class="flex h-full w-full flex-col overflow-hidden bg-base-200">
        <SubpageHeader
          title={t("catalog.nav.overview")}
          subtitle={t("catalog.overview.subtitle")}
        >
          <div role="tablist" class="tabs tabs-boxed tabs-sm">
            {PERIODS.map((p) => (
              <button
                key={p}
                role="tab"
                class={["tab", days.value === p ? "tab-active" : ""].join(" ")}
                onClick={() => (days.value = p)}
              >
                {t("catalog.overview.period_days").replace("{days}", String(p))}
              </button>
            ))}
          </div>
        </SubpageHeader>

        <div class="flex-1 overflow-auto p-3">
          {!data.value || !engineering.value ? (
            <div class="flex h-full items-center justify-center">
              <Loading />
            </div>
          ) : (
            <div
              class={[
                "space-y-3 transition-opacity",
                loading.value ? "opacity-60" : "",
              ].join(" ")}
            >
              <div class="flex flex-wrap gap-2">
                {quick(
                  "add",
                  t("catalog.overview.add_product"),
                  "/app/catalog_editor/products",
                )}
                {quick(
                  "create_new_folder",
                  t("catalog.overview.add_category"),
                  "/app/catalog_editor/categories",
                )}
                {quick(
                  "add_circle",
                  t("catalog.overview.edit_extras"),
                  "/app/catalog_editor/extras",
                )}
                {quick(
                  "schedule",
                  t("catalog.overview.edit_services"),
                  "/app/catalog_editor/services",
                )}
              </div>
              {renderKpis(data.value)}
              <div class="grid grid-cols-1 gap-3 xl:grid-cols-3">
                <div class="space-y-3 xl:col-span-2">
                  {card(
                    t("catalog.overview.attention"),
                    <AttentionItems
                      items={catalogAttention(data.value, t)}
                      emptyText={t("catalog.overview.all_clear")}
                    />,
                  )}
                  {card(
                    t("catalog.overview.best_sellers"),
                    <BestSellers
                      products={data.value.products}
                      total={data.value.sales.revenue}
                    />,
                    t("catalog.overview.last_days").replace(
                      "{days}",
                      String(data.value.days),
                    ),
                  )}
                  {card(
                    t("catalog.overview.menu_engineering"),
                    <MenuMatrix engineering={engineering.value} />,
                    t("catalog.overview.menu_engineering_hint"),
                  )}
                </div>
                <div class="space-y-3">
                  {card(
                    t("catalog.overview.sales_by_week"),
                    <WeeklyBars
                      weeks={data.value.sales.weeks}
                      label={t("catalog.overview.sales_by_week")}
                    />,
                  )}
                  {card(
                    t("catalog.overview.sales_by_category"),
                    <CategorySales categories={data.value.categories} />,
                  )}
                  {card(
                    t("catalog.overview.completeness"),
                    <MenuCompleteness setup={data.value.setup} />,
                  )}
                  {card(
                    t("catalog.overview.your_catalog"),
                    <CatalogGlance counts={data.value.counts} />,
                  )}
                  {card(
                    t("catalog.overview.slow_movers"),
                    <SlowMovers
                      unsold={data.value.gaps.unsold}
                      days={data.value.days}
                    />,
                    t("catalog.overview.slow_movers_hint").replace(
                      "{days}",
                      String(data.value.days),
                    ),
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  },
});
